(() => {
  const stateKey = '__dessonProductPageEnhancement';
  const state = window[stateKey] || {
    galleries: new WeakSet(),
    listenersRegistered: false,
    header: null,
    headerObserver: null,
    pendingGalleryStatusUpdates: new WeakSet(),
  };

  window[stateKey] = state;
  state.pendingGalleryStatusUpdates ||= new WeakSet();

  const getVisibleMediaItems = (gallery) =>
    Array.from(gallery.querySelectorAll('.product__media-item[data-media-id]')).filter(
      (item) => !item.hidden && !item.classList.contains('hidden') && item.getAttribute('aria-hidden') !== 'true'
    );

  const getActiveMediaItem = (gallery, mediaItems, slideDetail) => {
    const currentMediaId = slideDetail?.currentElement?.dataset?.mediaId;
    const currentMediaItem = mediaItems.find((item) => item.dataset.mediaId === currentMediaId);
    if (currentMediaItem) return currentMediaItem;

    const currentPage = Number(slideDetail?.currentPage);
    if (Number.isInteger(currentPage) && currentPage > 0 && currentPage <= mediaItems.length) {
      return mediaItems[currentPage - 1];
    }

    const activeThumbnail = gallery.querySelector('[data-target] button[aria-current="true"]');
    const activeMediaId = activeThumbnail?.closest('[data-target]')?.dataset.target;
    const thumbnailMediaItem = mediaItems.find((item) => item.dataset.mediaId === activeMediaId);
    if (thumbnailMediaItem) return thumbnailMediaItem;

    return mediaItems.find((item) => item.classList.contains('is-active')) || mediaItems[0];
  };

  const updateGalleryStatus = (gallery, slideDetail) => {
    const status = gallery.querySelector('[data-desson-media-status]');
    const mediaItems = getVisibleMediaItems(gallery);
    if (!status || !mediaItems.length) return;

    const activeMediaItem = getActiveMediaItem(gallery, mediaItems, slideDetail);
    const current = mediaItems.indexOf(activeMediaItem) + 1;
    const template = status.dataset.statusTemplate;

    if (template && current > 0) {
      status.textContent = template.replace('{{ current }}', current).replace('{{ total }}', mediaItems.length);
    }
  };

  const updateStickyOffset = () => {
    const header = document.querySelector('sticky-header, .section-header, .header-wrapper');
    if (!header) return null;

    const offset = header.getBoundingClientRect().bottom + 24;
    document.documentElement.style.setProperty('--desson-product-sticky-offset', `${offset}px`);
    return header;
  };

  const observeHeader = () => {
    const header = updateStickyOffset();
    if (!header || state.header === header) return;

    state.headerObserver?.disconnect();
    state.header = header;

    if ('ResizeObserver' in window) {
      state.headerObserver = new ResizeObserver(updateStickyOffset);
      state.headerObserver.observe(header);
    }
  };

  const initializeGallery = (gallery) => {
    if (state.galleries.has(gallery)) return;

    state.galleries.add(gallery);
    gallery.querySelector('[id^="GalleryViewer"]')?.addEventListener('slideChanged', (event) => {
      updateGalleryStatus(gallery, event.detail);
    });
    updateGalleryStatus(gallery);
  };

  const initialize = (root = document) => {
    if (root.matches?.('[data-desson-media-gallery]')) initializeGallery(root);
    root.querySelectorAll?.('[data-desson-media-gallery]').forEach(initializeGallery);
    observeHeader();
  };

  const queueGalleryStatusUpdate = (gallery) => {
    if (state.pendingGalleryStatusUpdates.has(gallery)) return;

    state.pendingGalleryStatusUpdates.add(gallery);
    window.setTimeout(() => {
      state.pendingGalleryStatusUpdates.delete(gallery);
      updateGalleryStatus(gallery);
    });
  };

  const queueAllGalleryStatusUpdates = () => {
    document.querySelectorAll('[data-desson-media-gallery]').forEach(queueGalleryStatusUpdate);
  };

  const updateAssuranceAvailability = ({ data } = {}) => {
    if (!data?.sectionId) return;

    const isAvailable = Boolean(data.variant?.available);
    document.querySelectorAll('[data-desson-product-assurance]').forEach((assurance) => {
      if (assurance.dataset.sectionId !== data.sectionId) return;

      const status = assurance.querySelector('[data-desson-availability-text]');
      if (!status) return;

      status.textContent = isAvailable ? assurance.dataset.availableText : assurance.dataset.unavailableText;
      assurance.dataset.availabilityState = isAvailable ? 'available' : 'unavailable';
    });
  };

  const handleDawnVariantChange = (event) => {
    queueAllGalleryStatusUpdates();
    updateAssuranceAvailability(event);
  };

  const subscribeToDawnVariantChanges = () => {
    if (
      state.variantChangeUnsubscribe ||
      typeof subscribe !== 'function' ||
      typeof PUB_SUB_EVENTS === 'undefined' ||
      !PUB_SUB_EVENTS.variantChange
    ) {
      return;
    }

    state.variantChangeUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, handleDawnVariantChange);
  };

  if (!state.listenersRegistered) {
    state.listenersRegistered = true;

    document.addEventListener('shopify:section:load', (event) => initialize(event.target));
    window.addEventListener('resize', observeHeader);
  }

  state.initialize = initialize;
  subscribeToDawnVariantChanges();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => state.initialize(), { once: true });
  } else {
    state.initialize();
  }
})();
